import { showErrorDialog } from '@juvare/common-web-ui/DialogHelper';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import DriveFolderUploadIcon from '@mui/icons-material/DriveFolderUpload';
import { Box, Button, Typography } from '@mui/material';
import React from 'react';
import { useDocumentLibraryTranslations } from '../../../../hooks';
import { maximumNumberOfFilesAllowed } from '../../contants';
export const FileDropBox = ({ extendedFiles, onSetFiles }) => {
    const { t } = useDocumentLibraryTranslations();
    const filesInput = React.useRef(null);
    const allowAddFiles = extendedFiles.length < maximumNumberOfFilesAllowed;
    const handleDrop = React.useCallback((event) => {
        event.preventDefault();
        const dragFiles = findValidFileInDragEvent(event, extendedFiles);
        if (dragFiles.length > 0 && dragFiles.length <= maximumNumberOfFilesAllowed) {
            onSetFiles(dragFiles);
        }
        else {
            showErrorDialog({
                title: t('multipleFilesUpload.dropArea.maximumFileNumberAllowed', { number: maximumNumberOfFilesAllowed }),
            });
        }
    }, [extendedFiles, onSetFiles, t]);
    const handleDragOver = React.useCallback((event) => {
        event.preventDefault();
    }, []);
    const handleDragEnter = React.useCallback((event) => {
        event.preventDefault();
    }, []);
    const handleDragLeave = React.useCallback((event) => {
        event.preventDefault();
    }, []);
    const handleFileChange = React.useCallback((event) => {
        if (event.target.files) {
            let changedFiles = extendedFiles;
            for (let i = 0; i < event.target.files.length; i++) {
                const file = event.target.files[i];
                if (file) {
                    changedFiles = addNewFile(changedFiles, file);
                }
            }
            if (changedFiles.length <= maximumNumberOfFilesAllowed) {
                onSetFiles(changedFiles);
            }
            else {
                showErrorDialog({
                    title: t('multipleFilesUpload.dropArea.maximumFileNumberAllowed', { number: maximumNumberOfFilesAllowed }),
                    onClose: () => undefined,
                });
            }
        }
    }, [extendedFiles, t, onSetFiles]);
    const handleSelectFileClick = React.useCallback(() => {
        if (filesInput.current) {
            filesInput.current.click();
        }
    }, []);
    return (React.createElement(Box, { sx: {
            boxSizing: 'border-box',
            width: '100%',
            height: 224,
            padding: (theme) => theme.spacing(1),
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            border: `1px dashed ${themeColors.border.main}`,
            borderRadius: '1px',
            backgroundColor: themeColors.background,
        }, onDrop: handleDrop, onDragOver: handleDragOver, onDragEnter: handleDragEnter, onDragLeave: handleDragLeave, onDragStart: handleDragEnter, onDragEnd: handleDragLeave },
        !allowAddFiles && (React.createElement(Typography, { sx: { pointerEvents: 'none' } }, t('multipleFilesUpload.dropArea.maximumFileNumberAllowed', { number: maximumNumberOfFilesAllowed }))),
        allowAddFiles && (React.createElement(React.Fragment, null,
            React.createElement(DriveFolderUploadIcon, { sx: { mb: 1 }, color: 'primary' }),
            React.createElement(Typography, { variant: 'body1', color: 'primary' }, t('multipleFilesUpload.dropArea.dropFilesHere')),
            React.createElement(Typography, { sx: { m: 2, color: themeColors.text.subtle }, variant: 'subtitle1' }, t('common.or')),
            React.createElement("input", { ref: filesInput, type: 'file', hidden: true, multiple: true, onChange: handleFileChange, "aria-label": t('multipleFilesUpload.inputBrowseFilesAriaLabel') }),
            React.createElement(Button, { variant: 'outlined', color: 'primary', onClick: handleSelectFileClick }, t('multipleFilesUpload.dropArea.browseFiles'))))));
};
function findValidFileInDragEvent(event, extendedFiles) {
    let eventFiles = extendedFiles;
    if (event.dataTransfer.items) {
        // Use DataTransferItemList interface to access the file(s)
        for (let i = 0; i < event.dataTransfer.items.length; i++) {
            // If dropped items aren't files, reject them
            if (event.dataTransfer.items[i].kind === 'file') {
                const file = event.dataTransfer.items[i].getAsFile();
                if (file) {
                    eventFiles = addNewFile(eventFiles, file);
                }
            }
        }
    }
    else if (event.dataTransfer) {
        // Use DataTransfer interface to access the file(s)
        for (let i = 0; i < event.dataTransfer.files.length; i++) {
            const file = event.dataTransfer.files[i];
            if (file) {
                eventFiles = addNewFile(eventFiles, file);
            }
        }
    }
    return eventFiles;
}
function addNewFile(extendedFiles, file) {
    const filteredFiles = extendedFiles.filter((item) => item.file.name !== file.name);
    filteredFiles.push({ file, editedName: file.name, errors: {} });
    return filteredFiles.map((file) => Object.assign(file, { editedName: file.file.name }));
}
